/**
 * @fileoverview Client-side feature flag service.
 *
 * Fetches the resolved feature flag states for the current user and exposes a
 * simple {@link FeatureFlagService#isEnabled} helper.  All gates default to
 * `false` when the user is unauthenticated or when the fetch fails, so callers
 * never need to handle null/undefined themselves.
 *
 * Usage:
 *
 *   import { featureFlags } from './FeatureFlagService.js';
 *
 *   if (featureFlags.isEnabled('advanced_audio_export')) {
 *       // show the new exporter
 *   }
 */

const API_URL = import.meta.env.VITE_API_URL || '/api';

export class FeatureFlagService {
    constructor() {
        /** @type {Object<string, boolean>} */
        this._flags = {};
    }

    /**
     * Fetches the effective feature flag states from the server and caches them.
     * Fetches user-specific flags when authenticated, or public defaults when guest.
     *
     * @param {string} [token] - The current JWT bearer token.
     * @returns {Promise<void>}
     */
    async refresh(token) {
        try {
            const url = (token && token !== 'mock-test-token')
                ? `${API_URL}/user/feature-flags`
                : `${API_URL}/feature-flags`;
            const headers = (token && token !== 'mock-test-token')
                ? { 'Authorization': `Bearer ${token}` }
                : {};

            const res = await fetch(url, { headers });

            if (res.ok) {
                this._flags = await res.json();
            } else {
                // console.warn('[FeatureFlagService] Failed to fetch flags, status:', res.status);
                this._flags = {};
            }
        } catch (err) {
            // console.error('[FeatureFlagService] Error fetching flags:', err);
            this._flags = {};
        }
    }

    /**
     * Returns whether a feature flag is enabled for the current user.
     * Defaults to `false` for any unknown or not-yet-loaded flag.
     *
     * @param {string} flagKey - The snake_case key of the flag (e.g. `'advanced_audio_export'`).
     * @returns {boolean}
     */
    isEnabled(flagKey) {
        return this._flags[flagKey] === true;
    }

    /**
     * Clears all cached flag states (e.g. on logout).
     */
    clear() {
        this._flags = {};
    }
}

/** Singleton instance used throughout the app. */
export const featureFlags = new FeatureFlagService();
